import Note from "@/components/atoms/Note";
import PhoneCallLink from "@/components/atoms/PhoneCallLink";
import { UpdateSelectPaginateAsync } from "@/components/atoms/Select";
import SoftBadge from "@/components/atoms/SoftBadge";
import UnknownBadge from "@/components/atoms/UnknownBadge";
import StoreCompanyNoteModal from "@/components/molecules/modals/company/StoreCompanyNoteModal";
import CompanyFiltersComponent from "@/components/molecules/table-filters/CompanyFilters";
import AllCompaniesHeader from "@/components/molecules/table-headers/AllCompaniesHeader";
import Table from "@/components/molecules/table/Table";
import UpdatableCell from "@/components/molecules/table/UpdatableCell";
import { formatInterlocutor } from "@/helpers/CompanyHelpers";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import useLoadOptions from "@/hooks/useLoadOptions";
import { useTableState } from "@/hooks/useTableState";
import Company, { CompanyFilters } from "@/interfaces/Company";
import CompanyStatus from "@/interfaces/CompanyStatus";
import User, { UserFilters, UserRoleType } from "@/interfaces/IUser";
import {
  useGetCompaniesQuery,
  useUpdateCompanyMutation,
} from "@/services/companies";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { faEye, faMessage } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { ElementRef, FC, useRef } from "react";
import { Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";

const CompaniesView: FC = () => {
  const StoreCompanyNoteRef =
    useRef<ElementRef<typeof StoreCompanyNoteModal>>(null);
  const currentUser = useSelector(selectCurrentUser);
  const [state, setState] = useTableState<Partial<TableState> & CompanyFilters>(
    "companies",
    {
      pagination: {
        pageIndex: 0,
        pageSize: 15,
      },
      globalFilter: "",
    },
  );

  const [updateCompanyAsync] = useUpdateCompanyMutation();

  const { loadCompanyStatusesOptions, loadUsersOptions } = useLoadOptions();

  const defaultAdditional = useRef<{ pageIndex: number }>({
    pageIndex: 0,
  }).current;

  const sdrAdditional = useRef<UserFilters>({
    pageIndex: 0,
    roles: ["SDR"],
    isActive: true,
  }).current;

  const aeAdditional = useRef<{ pageIndex: number; roles: UserRoleType[] }>({
    pageIndex: 0,
    roles: ["AE"],
  }).current;

  const onUpdate = (id: string, columnId: string, value: unknown) => {
    toast.promise(
      updateCompanyAsync([
        id,
        {
          [columnId]: value || null,
        },
      ]),
      {
        pending: "Modification de la colonne en cours...",
        success: {
          render() {
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                La colonne à bien été modifiée 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }

            return "Une erreur est survenue";
          },
        },
      }
    );
  };

  const debouncedQuery = useDebouncedValue(state.globalFilter, 500);
  const debouncedSiret = useDebouncedValue(state.siret, 500);
  const debouncedCity = useDebouncedValue(state.city, 500);
  const debouncedZipCode = useDebouncedValue(state.zipCode, 500);
  const debouncedName = useDebouncedValue(state.name, 500);
  const debouncedPhone = useDebouncedValue(state.phone, 500);
  const debouncedNotes = useDebouncedValue(state.notes, 500);

  const { data, isLoading, isFetching } = useGetCompaniesQuery({
    ...state.pagination,
    query: debouncedQuery,
    unknownStatus: state.unknownStatus,
    ...(state.sdrs && {
      sdrIds: state.sdrs.map((element) => element.id),
    }),
    ...(state.aes && {
      aeIds: state.aes.map((element) => element.id),
    }),
    statusIds: state.status?.map((element) => element.id),
    ...(state.companies && {
      companyIds: state.companies?.map((element) => element.id),
    }),
    activitySectorIds: state.activitySectors?.map((element) => element.id),
    excludedActivitySectorIds: state.excludedActivitySectors?.map(
      (element) => element.id,
    ),
    opcoIds: state.opcos?.map((element) => element.id),
    reminderDatetime: state.reminderDatetime,
    siret: debouncedSiret,
    city: debouncedCity,
    zipCode: debouncedZipCode,
    name: debouncedName,
    phone: debouncedPhone,
    notes: debouncedNotes,
    ...(state.sorting?.[0] && {
      sortBy: state.sorting[0].id,
      sortDirection: state.sorting[0].desc ? "desc" : "asc",
    }),
  });

  const columns = useRef<ColumnDef<Company>[]>([
    {
      header: "Actions",
      enableHiding: false,
      enableSorting: false,
      footer: (props) => props.column.id,
      accessorFn: ({ id }) => (
        <>
          <Link to={`/companies/${id}`} className="btn btn-primary btn-sm">
            <FontAwesomeIcon icon={faEye} />
          </Link>
          <Button
            variant="danger"
            size="sm"
            className="ms-2"
            onClick={() => StoreCompanyNoteRef.current?.showOrHiddenModal(id)}
          >
            <FontAwesomeIcon icon={faMessage} />
          </Button>
        </>
      ),
      id: "actions",
      cell: (info) => info.getValue(),
    },
    {
      header: "Dernière\xa0note",
      footer: (props) => props.column.id,
      meta: {
        headerProps: { style: { minWidth: "14.625rem" } },
        cellProps: {
          style: {
            maxWidth: 500,
          },
        },
      },
      accessorFn: ({ notes }) =>
        notes?.[0] ? <Note note={notes[0]} /> : <span>Aucune note</span>,
      id: "lastNote",
      cell: (info) => info.getValue(),
    },
    {
      header: "Date de rappel",
      footer: (props) => props.column.id,
      accessorFn: ({ reminderDatetime }) => reminderDatetime,
      id: "reminderDatetime",
      cell: ({ getValue, row, column }) => (
        <UpdatableCell
          value={getValue()}
          row={row}
          column={column}
          type="datetime-local"
          onUpdate={onUpdate}
        />
      ),
    },
    {
      header: "Télépro",
      footer: (props) => props.column.id,
      accessorFn: ({ sdr }) => sdr?.user,
      id: "sdr",
      cell: ({
        getValue,
        row: {
          original: { id },
        },
      }) => {
        const user = getValue() as User | null;
        return checkIfUserHasRoles(currentUser, ["GENERALMANAGER"]) ? (
          <UpdateSelectPaginateAsync
            loadOptions={loadUsersOptions}
            defaultOptions
            additional={sdrAdditional}
            getOptionLabel={(option) => option.fullname}
            getOptionValue={(option) => option.id.toString()}
            value={user}
            onChange={(v) => onUpdate(id, "sdrId", v?.id)}
          />
        ) : (
          <SoftBadge bg={user ? "info" : "warning"} className="me-2">
            {user?.fullname ?? "Inconnu"}
          </SoftBadge>
        );
      },
    },
    {
      header: "Commercial",
      footer: (props) => props.column.id,
      accessorFn: ({ ae }) => ae?.user,
      id: "ae",
      cell: ({
        getValue,
        row: {
          original: { id },
        },
      }) => {
        const user = getValue() as User | null;
        return checkIfUserHasRoles(currentUser, ["GENERALMANAGER"]) ? (
          <UpdateSelectPaginateAsync
            loadOptions={loadUsersOptions}
            defaultOptions
            additional={aeAdditional}
            getOptionLabel={(option) => option.fullname}
            getOptionValue={(option) => option.id.toString()}
            value={user}
            onChange={(v) => onUpdate(id, "aeId", v?.id)}
          />
        ) : (
          <SoftBadge bg={user ? "info" : "warning"} className="me-2">
            {user?.fullname ?? "Inconnu"}
          </SoftBadge>
        );
      },
    },
    {
      header: "Opco",
      footer: (props) => props.column.id,
      accessorFn: ({ activitySector }) => (
        <SoftBadge
          bg={activitySector?.opco?.backgroundColor ?? "danger"}
          textColor={activitySector?.opco?.textColor}
          className="me-2"
        >
          {activitySector?.opco?.name || "Inconnu"}
        </SoftBadge>
      ),
      id: "activitySector.opcoId",
      cell: (info) => info.getValue(),
    },
    {
      header: "Statut",
      footer: (props) => props.column.id,
      accessorFn: ({ status }) => status,
      id: "statusId",
      cell: ({
        getValue,
        row: {
          original: { id },
        },
      }) => (
        <UpdateSelectPaginateAsync
          loadOptions={loadCompanyStatusesOptions}
          defaultOptions
          additional={defaultAdditional}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id.toString()}
          value={getValue() as CompanyStatus}
          onChange={(v) => onUpdate(id, "statusId", v?.id)}
        />
      ),
    },
    {
      header: "Siret",
      footer: (props) => props.column.id,
      accessorFn: ({ siret }) => (siret ? siret : <UnknownBadge />),
      id: "siret",
      cell: (info) => info.getValue(),
    },
    {
      header: "Ville",
      footer: (props) => props.column.id,
      accessorFn: ({ city }) => (city ? city : <UnknownBadge />),
      id: "city",
      cell: (info) => info.getValue(),
    },
    {
      header: "Code postal",
      footer: (props) => props.column.id,
      accessorFn: ({ zipCode }) => (zipCode ? zipCode : <UnknownBadge />),
      id: "zipCode",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nom",
      footer: (props) => props.column.id,
      accessorFn: ({ name }) => name,
      id: "name",
      cell: (info) => info.getValue(),
    },
    {
      header: "Interlocuteur",
      footer: (props) => props.column.id,
      accessorFn: ({ interlocutorFirstname, interlocutorLastname }) =>
        formatInterlocutor(interlocutorFirstname, interlocutorLastname) ?? (
          <UnknownBadge />
        ),
      id: "interlocutor",
      cell: (info) => info.getValue(),
    },
    {
      header: "Téléphone",
      footer: (props) => props.column.id,
      accessorFn: ({ phone }) =>
        phone ? <PhoneCallLink number={phone} /> : <UnknownBadge />,
      id: "phone",
      cell: (info) => info.getValue(),
    },
    {
      header: "Secteur d'activité",
      footer: (props) => props.column.id,
      accessorFn: ({ activitySector }) =>
        activitySector?.name || <UnknownBadge />,
      id: "activitySectorId",
      cell: (info) => info.getValue(),
    },
  ]).current;

  return (
    <div className="formalys-page formalys-companies-page">
      <StoreCompanyNoteModal ref={StoreCompanyNoteRef} />
      <Table
        resource="companies"
        HeaderComponent={AllCompaniesHeader}
        FilterComponent={CompanyFiltersComponent}
        tableName={"companies"}
        columns={columns}
        smallFilters
        state={state}
        onStateChange={setState}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading || isFetching}
      />
    </div>
  );
};

export default CompaniesView;
